import { Box } from "@mui/material"
import { styled, alpha } from "@mui/material/styles"
import SearchIcon from "@mui/icons-material/Search"
import InputBase from "@mui/material/InputBase"
import { useEventContext } from "@/contexts/EventContext"
import { debounce } from "lodash"
import useIsMobile from "@/hooks/useIsMobile"
import { useEffect, useRef, useState } from "react"
import useIsTablet from "@/hooks/useIsTablet"

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}))

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}))

export default function SearchBar() {
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchString, setSearchString] = useState("")
  const { setSearchVal, setSearchActive, searchActive } = useEventContext()

  const handleSearchDebounce = debounce((value: string) => {
    if (value === "") {
      setSearchVal("")
    } else {
      setSearchVal("%" + value + "%")
    }
  }, 300)

  const handleInput = (value: string) => {
    setSearchString(value)
    handleSearchDebounce(value)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current?.contains(event.target as Node)) {
      setSearchActive(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  })

  return (
    <Box
      sx={{
        display: { md: "flex" },
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Search
        sx={{
          color: "text.secondary",
          height: "50px",
          display: "flex",
        }}
      >
        <SearchIconWrapper>
          <SearchIcon width="26px" height="26px" />
        </SearchIconWrapper>
        <StyledInputBase
          placeholder={isMobile || isTablet ? "" : "Search…"}
          value={
            (!searchActive && isMobile) || (!searchActive && isTablet)
              ? ""
              : searchString
          }
          inputProps={{ "aria-label": "search" }}
          onChange={(val) => handleInput(val.target.value)}
          onFocus={() => setSearchActive(true)}
          ref={inputRef}
        />
      </Search>
    </Box>
  )
}
